<script lang="ts">
	import type { HTMLImgAttributes } from 'svelte/elements';

	import { page } from '$app/state';
	import { thumbnailUrl } from '$lib';
	import ThumbnailImage from '$lib/components/ThumbnailImage.svelte';

	let { src, ...rest }: { src: string } & HTMLImgAttributes = $props();

	const zoomScale = 1.5;

	let imgElement = $state<HTMLImageElement>();
	let containerElement = $state<HTMLDivElement>();
	let isHovering = $state(false);
	let mousePosition = $state({ x: 0, y: 0 });
	let imgBounds = $state({ left: 0, top: 0, width: 0, height: 0 });
	let naturalDimensions = $state({ width: 0, height: 0 });
	let hasMouseMoved = $state(false);
	let currentLensSize = $derived(imgBounds.width / 6);

	let isCursorOnImage = $derived(
		hasMouseMoved &&
			mousePosition.x >= 0 &&
			mousePosition.y >= 0 &&
			mousePosition.x <= imgBounds.width &&
			mousePosition.y <= imgBounds.height,
	);

	let backgroundPosition = $derived({
		x: -mousePosition.x * zoomScale + currentLensSize,
		y: -mousePosition.y * zoomScale + currentLensSize,
	});

	function updateImageBounds() {
		if (!imgElement || !naturalDimensions.width) return;

		const containerRect = imgElement.getBoundingClientRect();
		const containerRatio = containerRect.width / containerRect.height;
		const imageRatio = naturalDimensions.width / naturalDimensions.height;

		let width, height, offsetX, offsetY;

		if (imageRatio > containerRatio) {
			// Image is wider - height will be less than container
			width = containerRect.width;
			height = containerRect.width / imageRatio;
			offsetX = 0;
			offsetY = (containerRect.height - height) / 2;
		} else {
			// Image is taller - width will be less than container
			width = containerRect.height * imageRatio;
			height = containerRect.height;
			offsetX = (containerRect.width - width) / 2;
			offsetY = 0;
		}

		imgBounds = {
			left: offsetX,
			top: offsetY,
			width: width,
			height: height,
		};
	}

	function handleMouseMove(event: MouseEvent) {
		if (!containerElement) return;

		hasMouseMoved = true;

		const containerRect = containerElement.getBoundingClientRect();
		const rawX = event.clientX - containerRect.left;
		const rawY = event.clientY - containerRect.top;

		mousePosition = {
			x: rawX - imgBounds.left,
			y: rawY - imgBounds.top,
		};
	}

	function handleMouseEnter() {
		isHovering = true;
		updateImageBounds();
	}

	function handleMouseLeave() {
		isHovering = false;
		hasMouseMoved = false;
	}

	function handleImageLoad(event: Event) {
		imgElement = event.currentTarget as HTMLImageElement;

		naturalDimensions = {
			width: imgElement.naturalWidth,
			height: imgElement.naturalHeight,
		};
		updateImageBounds();
	}
</script>

<svelte:window onresize={updateImageBounds} />

<div
	bind:this={containerElement}
	class={['relative inline-block select-none', rest.class, { 'cursor-none': isCursorOnImage }]}
	onmouseenter={handleMouseEnter}
	onmouseleave={handleMouseLeave}
	onmousemove={handleMouseMove}
	role="img"
>
	<ThumbnailImage onload={handleImageLoad} {src} {...rest} minSize={1800} />

	{#if isHovering && isCursorOnImage && imgElement && naturalDimensions.width > 0}
		{@const url = thumbnailUrl(page.data.store.media_base_url, src, 1800)}
		<!-- eslint-disable custom-rules/no-inline-styles -->
		<div
			class="pointer-events-none absolute z-20 hidden -translate-1/2 overflow-hidden border-2 border-black bg-no-repeat pointer-fine:block"
			style="
                width: {currentLensSize * 2}px;
                height: {currentLensSize * 2}px;
                left: {mousePosition.x + imgBounds.left}px;
                top: {mousePosition.y + imgBounds.top}px;
                background-image: image-set(url('{url.replace(
				/\.jpe?g$/g,
				'.webp',
			)}'), url('{url}'));
                background-size: {imgBounds.width * zoomScale}px {imgBounds.height * zoomScale}px;
                background-position: {backgroundPosition.x}px {backgroundPosition.y}px;
            "
		></div>
		<!-- eslint-enable custom-rules/no-inline-styles -->
	{/if}
</div>
